import {
  convertFromPixels,
  convertToPixels,
} from '@src/utility/videoTranscriptionUtils';
import { makeAutoObservable } from 'mobx';

import { AspectRatio } from '../../types.ts/video';
import { FONT_SIZE_VALUES } from '../../utility/general';

import type { ElementState } from '../../renderer/ElementState';
import type VideoCreatorStore from '../../stores/VideoCreatorStore';
import type { Video } from '../../types.ts/story';

export const DEFAULT_TEXT_CONFIG = {
  duration: 4,
  text: '',
  font_size: '32',
  width: '80%',
  font_family: 'Inter',
  font_weight: '600',
  fill_color: 'rgba(0, 0, 0, 1)',
  background_color: null,
  background_align_threshold: '0%',
  background_border_radius: '0%',
  shadow_color: null,
  shadow_blur: null,
  shadow_x: null,
  shadow_y: null,
};

export class TextProducer {
  private videoCreator: VideoCreatorStore;

  constructor(videoCreator: VideoCreatorStore) {
    this.videoCreator = videoCreator;
    makeAutoObservable(this);
  }
  config: ElementState['source'] = DEFAULT_TEXT_CONFIG;
  isPropsChanged = false;
  creatingNewText = false;

  initializeConfig() {
    const aspectRatio =
      this.videoCreator.currentVideo?.aspectRatio ||
      this.videoCreator.getDefaultAspectRatio();
    const defaultTextSetting =
      this.getBasicTextSettingByAspectRatio(aspectRatio);
    return {
      ...DEFAULT_TEXT_CONFIG,
      ...defaultTextSetting,
    };
  }

  getTextConfig() {
    return this.config;
  }

  getBasicTextSettingByAspectRatio(
    aspectRatio: Video['aspectRatio'] = this.videoCreator.currentVideo
      ?.aspectRatio || this.videoCreator.getDefaultAspectRatio(),
  ) {
    switch (aspectRatio) {
      case AspectRatio.AR_16_9:
        return {
          font_size: '32', // '5.5556 vh', // should be 32
          y: '60%',
          width: '76%',
        };
      case AspectRatio.AR_1_1:
        return {
          font_size: '30', //'5 vh', // should be 30
          y: '55%',
          width: '78%',
        };
      case AspectRatio.AR_9_16:
        return {
          font_size: '24', //'5 vh', // should be 24
          y: '45%',
          width: '84%',
        };
      default:
        return {
          font_size: '32', //'5.5556 vh', // should be 32
          y: '60%',
          width: '76%',
        };
    }
  }

  setDefaultTextConfig(source: ElementState | null = null) {
    const config = this.initializeConfig();
    console.log('Setting default text config', config);
    const defaultConfig = source || {
      ...config,
    };

    this.config = defaultConfig;
  }

  setTextConfig(config: ElementState['source'] | undefined) {
    const configuration: ElementState['source'] = {
      ...(config || this.initializeConfig()),
      text: config?.text || '',
    };
    if (config?.font_size) {
      // Exact px, not snapped to the presets: modifyProperty writes the whole
      // config back to the element, so a snapped size would resize it.
      configuration.font_size =
        this.convertToPx(config.font_size) ??
        this.getFontSize(config.font_size);
    } else {
      configuration.font_size = this.getFontSize(DEFAULT_TEXT_CONFIG.font_size);
    }
    this.config = configuration;
  }

  async modifyProperty(
    propertyMap: ElementState['source'],
    switched_template = false,
    configId = this.config.id,
  ) {
    const config = { ...this.config, ...propertyMap };
    if (!config.shadow_color) {
      config.shadow_color = null;
      delete config.shadow_blur;
      delete config.shadow_x;
      delete config.shadow_y;
    }

    this.setTextConfig(config);

    for (let property in config) {
      if (property === 'font_size') {
        config[property] = this.convertToVh(config[property], config.id);
      }
    }
    this.videoCreator.textBrandingService.checkPropsChanged(
      'basic',
      propertyMap,
      switched_template,
    );

    if (!configId) return;

    // Use headless editor command to update element properties
    const editorStore = this.videoCreator.rootStore.editorStore;
    if (editorStore?.editor) {
      editorStore.editor.commandUpdateElement(configId, config);
    }
  }

  getFontSize(fontSize: string | number, _elementId?: string) {
    if (typeof fontSize === 'number') return this.findClosestValue(fontSize);

    const { width, height } = this.getDimensionsForElement();
    if (fontSize?.includes('vh')) {
      const value = convertToPixels(parseFloat(fontSize), 'vh', {
        width,
        height,
      });
      return this.findClosestValue(value);
    } else if (fontSize?.includes('vw')) {
      const value = convertToPixels(parseFloat(fontSize), 'vw', {
        width,
        height,
      });
      return this.findClosestValue(value);
    }

    return this.findClosestValue(parseInt(fontSize));
  }

  /**
   * The vh/vw basis for a text element's font_size: always the root video.
   *
   * Creatomate resolves `vh`/`vw` against the whole template, never against
   * the composition an element sits in (only `%` is composition-relative;
   * docs/template-editor/unit-system). PLAT-9951 had made this the containing
   * composition's size for nested text to cure "the first edit shrinks the
   * text" — but that shrink came from writing the 32 px default over an
   * auto-sized element, and the smaller basis only masked it by inflating the
   * vh. Against the renderer it was wrong in both directions: a nested
   * "4.68 vh" (33.7 px on screen) read back as 9 px, and writing 33 px to a
   * card a quarter of the frame high sent "17 vh" — 123 px (PLAT-12088).
   * `elementId` stays on the public conversions for their callers; the
   * basis no longer depends on it.
   */
  private getDimensionsForElement(): { width: number; height: number } {
    const source = this.videoCreator.rootStore.videoSourceStore.source;
    return { width: source?.width || 1280, height: source?.height || 720 };
  }

  private findClosestValue(value: number) {
    if (FONT_SIZE_VALUES.includes(value)) return value;

    let minDifference = Infinity;
    let closestValue = FONT_SIZE_VALUES[0];

    for (let i = 0; i < FONT_SIZE_VALUES.length; i++) {
      let difference = Math.abs(FONT_SIZE_VALUES[i] - value);

      if (difference < minDifference) {
        minDifference = difference;
        closestValue = FONT_SIZE_VALUES[i];
      }
    }

    return closestValue;
  }

  convertToVh(px: string | null, _elementId?: string) {
    const { width, height } = this.getDimensionsForElement();
    const value = convertFromPixels(parseFloat(px || '32'), 'vh', {
      width,
      height,
    });
    return `${value} vh`;
  }

  /**
   * Inverse of convertToVh, for display: resolves a stored font_size value
   * (number, "Npx", "N vh", "N vw", or a plain numeric string) to pixels
   * against the same dimension basis used when writing. Exact — unlike
   * getFontSize it does not snap to the text-settings slider presets.
   * Returns null when the value is missing or unparseable.
   */
  convertToPx(
    fontSize: string | number | null | undefined,
    elementId?: string,
  ): number | null {
    // No explicit font_size on the source (auto-sized text): fall back to the
    // renderer's computed size so the field still shows the effective value.
    if (fontSize == null || fontSize === '') {
      return this.getRenderedFontSizePx(elementId);
    }
    if (typeof fontSize === 'number') return fontSize;
    const parsed = parseFloat(fontSize);
    if (!Number.isFinite(parsed)) return null;
    const { width, height } = this.getDimensionsForElement();
    if (fontSize.includes('vh')) {
      return convertToPixels(parsed, 'vh', { width, height });
    }
    if (fontSize.includes('vw')) {
      return convertToPixels(parsed, 'vw', { width, height });
    }
    return parsed;
  }

  /**
   * Creatomate's computed ("fixed or auto-calculated") font size for an
   * element, read from the live renderer state — the only place the effective
   * size of auto-sized text exists.
   */
  private getRenderedFontSizePx(elementId?: string): number | null {
    if (!elementId) return null;
    const walk = (elements?: ElementState[]): number | null => {
      for (const el of elements ?? []) {
        if (el.source?.id === elementId) {
          return typeof el.fontSize === 'number' ? el.fontSize : null;
        }
        const nested = walk(el.elements);
        if (nested != null) return nested;
      }
      return null;
    };
    return walk(this.videoCreator.state?.elements);
  }
}
